import { createClient } from '@supabase/supabase-js';

// Supabase credentials come EXCLUSIVELY from build-time env vars
// (REACT_APP_SUPABASE_URL / _ANON_KEY). We intentionally do NOT ship a
// hard-coded fallback: a baked-in key can't be rotated without a code change,
// and the previous literal now lives in git history (rotate that anon key).
// Set these in Cloudflare Pages (Production AND Preview) and in a local
// frontend/.env for dev — see DEPLOYMENT.md and frontend/.env.example.
// Read the member expression DIRECTLY. CRA/craco (webpack DefinePlugin)
// replaces `process.env.REACT_APP_*` with the literal value at build time, so
// there is no runtime `process` reference to guard against. A
// `typeof process !== 'undefined'` guard is actively HARMFUL here: bare
// `process` is undefined in the browser bundle, so the guard short-circuits to
// '' even when the vars ARE set — which silently blanked the app once the
// hardcoded fallback was removed.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Fail loud rather than silently boot against the wrong/no project.
  throw new Error(
    'Supabase credentials missing: set REACT_APP_SUPABASE_URL and ' +
      'REACT_APP_SUPABASE_ANON_KEY in your environment (see DEPLOYMENT.md).'
  );
}

// IMPORTANT: Some hosting environments (Cloudflare RUM, PostHog session
// recording) inject fetch wrappers that read response bodies before our code
// can. That causes supabase-js to throw "body stream already read" when it
// tries to parse the response. We mitigate this in two ways:
//   1. We use a "pristine" fetch reference captured in index.html BEFORE any
//      third-party scripts run (window.__pristineFetch).
//   2. We re-materialize each Response from its ArrayBuffer so subsequent
//      readers always get a fresh body stream.
const baseFetch =
  (typeof window !== 'undefined' && window.__pristineFetch) ||
  (typeof window !== 'undefined' && window.fetch) ||
  (typeof globalThis !== 'undefined' && globalThis.fetch);

// Status codes for which the Response MUST have a null body (per spec).
// Materializing a body and constructing a new Response would throw for
// these (e.g. DELETE typically returns 204 No Content).
const NO_BODY_STATUSES = new Set([101, 103, 204, 205, 304]);

const safeFetch = async (input, init) => {
  const response = await baseFetch(input, init);
  // Pass through empty-body responses unchanged.
  if (NO_BODY_STATUSES.has(response.status)) {
    return response;
  }
  try {
    const buffer = await response.arrayBuffer();
    return new Response(buffer, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  } catch (e) {
    // If we can't materialize, fall back to original response.
    return response;
  }
};

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'kocore.auth',
  },
  global: {
    fetch: safeFetch,
  },
});

export default supabase;
